import React from 'react';
import { Link2 } from 'lucide-react';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { ChoiceGroup } from '../ChoiceGroup';
import { mockData } from '../../../services/mockData';
import { trainingProducts } from '../../../data/trainingProducts';
import { courseCharacteristics, courseCommissions, courseStatusLabels, courseVenues } from '../../../data/trainingCourseOptions';
import { getClientSites } from '../../../utils/clientSites';
import { getTrainedCourses } from '../../../utils/trainingCourses';
import { CourseCharacteristic, CourseCommission, CourseErrors, CourseStatus, CourseVenue, TrainingCourse, TrainingCourseDraft } from '../../../types/trainingCourse';
interface CourseDetailsFieldsProps {
  draft: TrainingCourseDraft;
  onChange: (changes: Partial<TrainingCourseDraft>) => void;
  errors: CourseErrors;
  courses: TrainingCourse[];
  isEditing?: boolean;
}
export function CourseDetailsFields({
  draft,
  onChange,
  errors,
  courses,
  isEditing = false
}: CourseDetailsFieldsProps) {
  const isOnDemand = draft.status === 'ondemand';
  const clientOptions = mockData.getClients().map(c => ({
    value: c.name,
    label: c.name
  }));
  const productOptions = trainingProducts.map(p => ({
    value: p.code,
    label: `${p.type} — ${p.name} · ${p.code}`
  }));
  const clientSites = getClientSites(draft.clientName);
  const siteOptions = draft.clientSite && !clientSites.includes(draft.clientSite) ? [...clientSites, draft.clientSite] : clientSites;
  // "Sede cliente" is not available for SEFORM commissions
  const venueOptions = courseVenues.filter(v => !(v.value === 'cliente' && draft.commission === 'SEFORM'));
  const leadOptions = courses.filter(c => c.characteristic === 'Trainante' && c.id !== draft.id).map(c => ({
    value: c.id,
    label: `${c.courseCode} — ${c.name}`
  }));
  const trainedCourses = getTrainedCourses(courses, draft.id);
  const handleStatus = (status: CourseStatus) => {
    onChange({
      status,
      // Restore defaults when leaving "on-demand"
      ...(status !== 'ondemand' && !draft.commission ? {
        commission: 'SEFORM' as CourseCommission
      } : {}),
      ...(status !== 'ondemand' && !draft.venue ? {
        venue: 'seform' as CourseVenue
      } : {})
    });
  };
  const handleCommission = (commission: CourseCommission) => {
    onChange({
      commission,
      ...(commission === 'SEFORM' ? {
        clientName: undefined,
        clientSite: undefined
      } : {}),
      ...(commission === 'SEFORM' && draft.venue === 'cliente' ? {
        venue: 'seform' as CourseVenue
      } : {})
    });
  };
  const handleCharacteristic = (characteristic: CourseCharacteristic) => {
    onChange({
      characteristic,
      leadCourseId: characteristic === 'Trainato' ? draft.leadCourseId : undefined
    });
  };
  return <div className="space-y-8" data-id="element-1146" data-mp="yovwiho9bvfy">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1147" data-mp="2j8hj28zacr88">
        <div className="col-span-full" data-id="element-1148" data-mp="2j6y0gbq5q43a">
          <Input label="Nome corso" placeholder="Es. Formazione generale lavoratori — Sessione ottobre" value={draft.name} onChange={e => onChange({
          name: e.target.value
        })} data-id="element-1149" data-mp="2eaz9qhhcw9zp" />
        </div>
        <div className="col-span-full" data-id="element-1150" data-mp="2o6aogzsrlh7g">
          <SearchableSelect label="Tipologia di corso + codice prodotto" value={draft.productCode} onChange={e => onChange({
          productCode: e.target.value
        })} options={productOptions} placeholder="Cerca tipologia o codice prodotto..." error={errors.productCode} required data-id="element-1151" data-mp="31z8xhoy4r7th" />
        </div>
        <div data-id="element-1152" data-mp="1hjx6s54ocqwg">
          <Input label="Codice specifico del corso" placeholder="Es. FGL-2026-10" value={draft.courseCode} onChange={e => onChange({
          courseCode: e.target.value
        })} error={errors.courseCode} required data-id="element-1153" data-mp="1hjf68wazbqiq" />
          <label className="mt-3 flex items-center gap-2 cursor-pointer w-fit" data-id="element-1154" data-mp="8mqf94bc2zzt">
            <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={draft.externalProduct} onChange={e => onChange({
            externalProduct: e.target.checked
          })} data-id="element-1155" data-mp="8otemeydm5h7" />
            <span className="text-sm text-slate-700" data-id="element-1156" data-mp="3h4mssn58j4v3">
              Acquisto prodotto esterno
            </span>
          </label>
        </div>
        {isEditing && isOnDemand ?
      // On-demand is chosen only at creation and cannot be changed later
      <div data-id="element-1157" data-mp="3uugnvn7c2ld9">
            <span className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1158" data-mp="3uv032w5lmvnl">
              Stato del corso
            </span>
            <div className="rounded-xl border-2 border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-700" aria-readonly="true" data-id="element-1159" data-mp="2ya7uz0unti8u">
              {courseStatusLabels.ondemand}
            </div>
            <p className="text-xs text-slate-500 mt-1.5" data-id="element-1160" data-mp="2pus5rwq7y7oq">
              Un corso On-demand non può cambiare stato
            </p>
          </div> : <Select label="Stato del corso" value={draft.status} onChange={e => handleStatus(e.target.value as CourseStatus)} options={(Object.keys(courseStatusLabels) as CourseStatus[]
      // In edit mode a dated course cannot become On-demand
      ).filter(s => !(isEditing && s === 'ondemand')).map(s => ({
        value: s,
        label: courseStatusLabels[s]
      }))} description={isEditing ? 'Non può diventare On-demand: questa opzione si sceglie solo alla creazione' : undefined} data-id="element-1161" data-mp="2lvt18d8egcxt" />}
        {!isOnDemand && <>
            <Input label="Data inizio corso" type="date" value={draft.startDate || ''} onChange={e => onChange({
          startDate: e.target.value || undefined
        })} data-id="element-1162" data-mp="1o6pwr9gotlud" />
            <Input label="Data fine corso" type="date" value={draft.endDate || ''} min={draft.startDate} onChange={e => onChange({
          endDate: e.target.value || undefined
        })} error={errors.endDate} data-id="element-1163" data-mp="14k61oqrptuwl" />
          </>}
      </div>

      <div className="h-px bg-slate-100" data-id="element-1164" data-mp="2286c5dqt63fw"></div>

      <section className="space-y-4" data-id="element-1165" data-mp="229zi0qtlkjmu">
        <ChoiceGroup label="Caratteristica del corso" name="course-characteristic" value={draft.characteristic} options={courseCharacteristics.map(c => ({
        value: c,
        label: c
      }))} onChange={handleCharacteristic} data-id="element-1166" data-mp="1njjzjjskfpsd" />
        {draft.characteristic === 'Trainante' && <div className="rounded-xl bg-slate-50 border border-slate-200 p-4" data-id="element-1167" data-mp="2kz67cwowc9hm">
            <p className="text-sm font-medium text-seform-navy flex items-center gap-2 mb-2" data-id="element-1168" data-mp="9injxdgrefat">
              <Link2 className="h-4 w-4" data-id="element-1169" data-mp="4kubedrzh97d" /> Corsi trainati
            </p>
            {trainedCourses.length > 0 ? <ul className="space-y-1.5" data-id="element-1170" data-mp="j875iqvuwn9d">
                {trainedCourses.map(c => <li key={c.id} className="text-sm text-slate-700" data-id="element-1171" data-mp="eb6vy5zst2di">
                    <span className="font-mono text-xs text-slate-500 mr-2" data-id="element-1172" data-mp="3mpp9uocf7c51">
                      {c.courseCode}
                    </span>
                    {c.name}
                  </li>)}
              </ul> : <p className="text-sm text-slate-500" data-id="element-1173" data-mp="3vrf37zpv4l5g">
                Nessun corso trainato. I corsi che sceglieranno questo corso
                come trainante compariranno qui in automatico.
              </p>}
          </div>}
        {draft.characteristic === 'Trainato' && <SearchableSelect label="Corso trainante" value={draft.leadCourseId || ''} onChange={e => onChange({
        leadCourseId: e.target.value
      })} options={leadOptions} placeholder="Cerca e seleziona il corso trainante..." error={errors.leadCourseId} required data-id="element-1174" data-mp="16y1gt40j23ri" />}
      </section>

      {!isOnDemand && draft.commission && draft.venue && <>
          <div className="h-px bg-slate-100" data-id="element-1175" data-mp="3j89wgq35zt9t"></div>
          <section className="space-y-4" data-id="element-1176" data-mp="3j7hyx9q0blgw">
            <ChoiceGroup label="Commessa" name="course-commission" value={draft.commission} options={courseCommissions} onChange={handleCommission} data-id="element-1177" data-mp="3o6umxxsm6yl2" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1178" data-mp="1yv16rwo50jc">
              {draft.commission === 'Cliente' && <SearchableSelect label="Azienda" value={draft.clientName || ''} onChange={e => onChange({
            clientName: e.target.value,
            clientSite: undefined
          })} options={clientOptions} placeholder="Cerca e seleziona un'azienda..." error={errors.clientName} required data-id="element-1179" data-mp="1ytr6nqlzl7n0" />}
              <Select label="Sede" value={draft.venue} onChange={e => onChange({
            venue: e.target.value as CourseVenue,
            clientSite: e.target.value === 'cliente' ? draft.clientSite : undefined
          })} options={venueOptions} data-id="element-1180" data-mp="72xa66ka9dqp" />
              {draft.venue === 'cliente' && <Select label="Sede del cliente" value={draft.clientSite || ''} onChange={e => onChange({
            clientSite: e.target.value
          })} disabled={!draft.clientName} error={errors.clientSite} description={draft.clientName ? 'Sede legale e sedi operative da anagrafica' : "Seleziona prima l'azienda"} options={[{
            value: '',
            label: 'Seleziona sede...'
          }, ...siteOptions.map(s => ({
            value: s,
            label: s
          }))]} required data-id="element-1181" data-mp="3alqh40ik34bw" />}
            </div>
          </section>
        </>}
    </div>;
}